import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, XCircle } from '@phosphor-icons/react';

const OAuthCallback = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing OAuth callback...');
  const [error, setError] = useState(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        setStatus('processing');
        setMessage('Processing OAuth callback...');

        // Check if this is a successful OAuth callback from the backend
        const urlParams = new URLSearchParams(window.location.search);
        const connected = urlParams.get('connected');
        
        if (connected === 'google' || connected === 'facebook') {
          setStatus('success');
          setMessage(`OAuth authorization successful for ${connected}!`);
          
          // Wait a moment to show success message
          setTimeout(() => {
            // Navigate back to Account Settings
            navigate('/dashboard/settings', { 
              state: { 
                oauthSuccess: true,
                service: connected
              } 
            });
          }, 2000);
        } else {
          // Check for error parameters
          const errorParam = urlParams.get('error');
          if (errorParam) {
            throw new Error(`OAuth error: ${errorParam}`);
          } else {
            throw new Error('Invalid OAuth callback - no success or error parameter found');
          }
        }

      } catch (err) {
        console.error('OAuth callback error:', err);
        setStatus('error');
        setError(err.message);
        setMessage('OAuth authorization failed');
      }
    };

    processCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {status === 'processing' && (
            <div className="flex items-center justify-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}
          
          {status === 'success' && (
            <div className="flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          )}
          
          {status === 'error' && (
            <div className="flex items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          )}

          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {status === 'processing' && 'Processing Authorization...'}
            {status === 'success' && 'Authorization Successful'}
            {status === 'error' && 'Authorization Failed'}
          </h2>
          
          <p className="text-gray-600 mb-6">{message}</p>
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center text-red-800">
                <XCircle className="w-5 h-5 mr-2" />
                <span className="font-medium">Error Details:</span>
              </div>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-800 text-sm">
                Your Google account has been successfully authorized for API access. 
                You will be redirected to the Account Settings page.
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-3">
              <button
                onClick={() => navigate('/dashboard/settings')}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Return to Account Settings
              </button>
              
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OAuthCallback;
